import { useEffect, useState } from "react";
import {
  Database, Play, ArrowsClockwise, CheckCircle, Warning, Clock, WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Data Migrations Panel (OPS-001 — Data Migration Standard).
 *
 * Administrator-only UI for the platform migration framework. Lists every
 * registered migration, its most recent audit run, and provides Dry-Run and
 * Apply actions. Every action creates a row in db.migration_runs — the audit
 * log is always visible below.
 *
 * DSG-001 compliant: no hard-coded colour/typography/radius literals.
 */
export default function MigrationsPanel() {
  const [migrations, setMigrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState({});
  const [historyOpenFor, setHistoryOpenFor] = useState(null);
  const [history, setHistory] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/migrations");
      setMigrations(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load migrations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const loadHistory = async (name) => {
    setHistoryOpenFor(name);
    setHistory([]);
    try {
      const r = await api.get(`/admin/migrations/${name}/history`);
      setHistory(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load history");
    }
  };

  const runMigration = async (name, dryRun) => {
    const key = `${name}:${dryRun ? "dry" : "apply"}`;
    setRunning((s) => ({ ...s, [key]: true }));
    try {
      const r = await api.post(`/admin/migrations/${name}/run`, { dry_run: dryRun });
      const audit = r.data;
      const verb = dryRun ? "Dry-run" : "Migration";
      if (audit.status === "success") {
        toast.success(
          `${verb} complete · scanned ${audit.scanned}, created ${audit.created}, skipped ${audit.skipped}`
        );
      } else if (audit.status === "partial") {
        toast.warning(
          `${verb} partial · created ${audit.created}, failed ${audit.failed}`
        );
      } else {
        toast.error(`${verb} failed · see audit log`);
      }
      await load();
      if (historyOpenFor === name) await loadHistory(name);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${dryRun ? "Dry-run" : "Migration"} failed`);
    } finally {
      setRunning((s) => ({ ...s, [key]: false }));
    }
  };

  return (
    <div data-testid="admin-panel-migrations">
      <SectionHeader
        title="Data Migrations"
        description="Administrator-only migration framework (OPS-001). Every action is idempotent, non-destructive and fully audited."
      />

      {loading ? (
        <div className="rounded-md border border-divider bg-surface p-6 text-sm text-ink-muted">
          Loading migrations…
        </div>
      ) : migrations.length === 0 ? (
        <div className="rounded-md border border-divider bg-surface p-6 text-sm text-ink-muted">
          No migrations registered.
        </div>
      ) : (
        <ul className="space-y-4" data-testid="migrations-list">
          {migrations.map(({ definition, last_run }) => {
            const dryKey = `${definition.name}:dry`;
            const applyKey = `${definition.name}:apply`;
            return (
              <li
                key={definition.name}
                data-testid={`migration-card-${definition.name}`}
                className="rounded-md border border-divider bg-surface shadow-token-sm"
              >
                <div className="flex flex-col gap-4 border-b border-divider-subtle px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Database size={16} weight="regular" className="text-ink-secondary" />
                      <h3 className="font-display text-base font-semibold text-charcoal">
                        {definition.title}
                      </h3>
                      <span className="rounded-full border border-divider bg-surface-alt px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ink-secondary">
                        v{definition.version}
                      </span>
                    </div>
                    <p className="mt-1.5 max-w-3xl text-sm text-ink-secondary">
                      {definition.description}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
                      <span className="font-mono">source: db.{definition.source_collection}</span>
                      <span>→</span>
                      <span className="font-mono">target: db.{definition.target_collection}</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <button
                      data-testid={`migration-dryrun-${definition.name}`}
                      onClick={() => runMigration(definition.name, true)}
                      disabled={running[dryKey] || running[applyKey]}
                      className="inline-flex items-center gap-1.5 rounded-md border border-divider-strong bg-surface px-3 py-1.5 text-sm font-medium text-ink-secondary hover:bg-surface-alt disabled:opacity-50"
                    >
                      <ArrowsClockwise size={14} weight="bold" /> Dry-Run
                    </button>
                    <button
                      data-testid={`migration-apply-${definition.name}`}
                      onClick={() => runMigration(definition.name, false)}
                      disabled={running[dryKey] || running[applyKey]}
                      className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-sm font-semibold text-brand-foreground hover:bg-brand-hover disabled:opacity-50"
                    >
                      <Play size={14} weight="bold" /> Apply
                    </button>
                  </div>
                </div>

                {/* Last-run summary strip */}
                <div className="flex flex-wrap items-center gap-x-6 gap-y-1 px-5 py-2.5 text-xs text-ink-secondary">
                  {last_run ? (
                    <>
                      <StatusChip status={last_run.status} mode={last_run.mode} />
                      <Metric label="Scanned" value={last_run.scanned} />
                      <Metric label="Created" value={last_run.created} tone="success" />
                      <Metric label="Skipped" value={last_run.skipped} />
                      <Metric label="Failed" value={last_run.failed} tone={last_run.failed ? "error" : "muted"} />
                      <span className="text-ink-muted">
                        {new Date(last_run.finished_at || last_run.started_at).toLocaleString("en-CA", {
                          month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                        })}
                        {" · by "}
                        <span className="font-medium text-ink-secondary">{last_run.executed_by}</span>
                      </span>
                      <button
                        data-testid={`migration-history-${definition.name}`}
                        onClick={() => (historyOpenFor === definition.name ? setHistoryOpenFor(null) : loadHistory(definition.name))}
                        className="ml-auto text-xs font-medium text-brand hover:underline"
                      >
                        {historyOpenFor === definition.name ? "Hide history" : "View history"}
                      </button>
                    </>
                  ) : (
                    <span className="italic text-ink-muted">Never run.</span>
                  )}
                </div>

                {/* History panel */}
                {historyOpenFor === definition.name && (
                  <div className="border-t border-divider-subtle px-5 py-3" data-testid={`migration-history-panel-${definition.name}`}>
                    <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-ink-muted">
                      Audit history (most recent first)
                    </h4>
                    {history.length === 0 ? (
                      <p className="text-xs text-ink-muted">No history yet.</p>
                    ) : (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-divider-subtle text-left text-ink-muted">
                            <th className="py-1.5 pr-3 font-semibold">When</th>
                            <th className="py-1.5 pr-3 font-semibold">Mode</th>
                            <th className="py-1.5 pr-3 font-semibold">Status</th>
                            <th className="py-1.5 pr-3 font-semibold">By</th>
                            <th className="py-1.5 pr-3 text-right font-semibold">Scan</th>
                            <th className="py-1.5 pr-3 text-right font-semibold">Create</th>
                            <th className="py-1.5 pr-3 text-right font-semibold">Skip</th>
                            <th className="py-1.5 pr-3 text-right font-semibold">Fail</th>
                          </tr>
                        </thead>
                        <tbody>
                          {history.map((h) => (
                            <tr key={h.id} className="border-b border-divider-subtle last:border-0">
                              <td className="py-1.5 pr-3 font-mono">
                                {new Date(h.started_at).toLocaleString("en-CA", {
                                  month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                                })}
                              </td>
                              <td className="py-1.5 pr-3 font-mono">{h.mode}</td>
                              <td className="py-1.5 pr-3"><StatusChip status={h.status} compact /></td>
                              <td className="py-1.5 pr-3">{h.executed_by}</td>
                              <td className="py-1.5 pr-3 text-right font-mono">{h.scanned}</td>
                              <td className="py-1.5 pr-3 text-right font-mono">{h.created}</td>
                              <td className="py-1.5 pr-3 text-right font-mono">{h.skipped}</td>
                              <td className={`py-1.5 pr-3 text-right font-mono ${h.failed ? "text-status-error-fg" : ""}`}>{h.failed}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {history.some((h) => (h.errors || []).length) && (
                      <div className="mt-3 rounded-md border border-status-error-bg bg-status-error-bg/50 p-2.5 text-xs text-status-error-fg">
                        <div className="mb-1 flex items-center gap-1 font-semibold">
                          <WarningCircle size={12} weight="bold" /> Recent errors
                        </div>
                        <ul className="list-inside list-disc space-y-0.5">
                          {history
                            .flatMap((h) => (h.errors || []).slice(0, 3).map((e) => ({ id: h.id, e })))
                            .slice(0, 5)
                            .map(({ id, e }, i) => (
                              <li key={`${id}-${i}`} className="font-mono">{e}</li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function SectionHeader({ title, description }) {
  return (
    <div className="mb-5 border-b border-divider pb-3">
      <h2 className="font-display text-lg font-semibold text-charcoal">{title}</h2>
      <p className="mt-0.5 text-sm text-ink-muted">{description}</p>
    </div>
  );
}

function Metric({ label, value, tone = "default" }) {
  const cls = {
    default: "text-ink-secondary",
    success: value > 0 ? "text-status-success-fg" : "text-ink-muted",
    error:   value > 0 ? "text-status-error-fg" : "text-ink-muted",
    muted:   "text-ink-muted",
  }[tone];
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className="text-[10px] uppercase tracking-widest text-ink-muted">{label}</span>
      <span className={`font-mono text-sm font-semibold ${cls}`}>{value}</span>
    </span>
  );
}

function StatusChip({ status, mode, compact }) {
  const map = {
    success:     { icon: CheckCircle,    cls: "bg-status-success-bg text-status-success-fg", label: "Success" },
    partial:     { icon: Warning,        cls: "bg-status-warning-bg text-status-warning-fg", label: "Partial" },
    failed:      { icon: WarningCircle,  cls: "bg-status-error-bg   text-status-error-fg",   label: "Failed"  },
    in_progress: { icon: Clock,          cls: "bg-status-info-bg    text-status-info-fg",    label: "Running" },
  };
  const cfg = map[status] || map.in_progress;
  const Icon = cfg.icon;
  const modeLabel = mode === "dry_run" ? "Dry-Run" : mode === "apply" ? "Apply" : null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${cfg.cls}`}>
      <Icon size={11} weight="bold" />
      {cfg.label}{!compact && modeLabel ? ` · ${modeLabel}` : ""}
    </span>
  );
}
